import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { sort } from '@ember/object/computed';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import set from 'ember-set-helper/helpers/set';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import eq from 'lodash/eq';
import moment from 'moment-timezone';
import TasksKitFiltersComponent from 'volta/components/tasks-kit/filters';
import TasksKitListDateComponent from 'volta/components/tasks-kit/list/date';
import TasksKitListEntityComponent from 'volta/components/tasks-kit/list/entity';
import TasksKitSubmitBtnComponent from 'volta/components/tasks-kit/submit-btn';
import TasksKitViewSelectComponent from 'volta/components/tasks-kit/view-select';
import { indexedEntities, TaskEvents, TaskResourceNameForEntityType } from 'volta/models/task';
import TaskService from 'volta/services/task-service';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { formatLocalDate, ISO_DATE_FORMAT, LOCAL_DATE_FORMAT } from 'volta/utils/date-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import TasksKitListDateEntityComponent from './list/date-entity';
import type Owner from '@ember/owner';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type { IntlService } from 'ember-intl';
import type { TasksKitFiltersSignature } from 'volta/components/tasks-kit/filters';
import type { TasksKitSubmitBtnSignature } from 'volta/components/tasks-kit/submit-btn';
import type { TasksKitViewSelectSignature } from 'volta/components/tasks-kit/view-select';
import type { IResourceName } from 'volta/models/base-model';
import type { TaskEntityType } from 'volta/models/task';
import type { TDate } from 'volta/models/types/calendar';
import type { TTaskType, UTask } from 'volta/models/types/tasks';
import type BvFlashService from 'volta/services/bv-flash';
import type EventStreamService from 'volta/services/event-stream';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type AuthorizationService from 'volta/services/authorization';
export type TSelectedView = 'open' | 'resolved';
export type TTaskListVariation = 'entity' | 'date-entity' | 'date';
type EntityList = WithBoundArgs<typeof TasksKitListEntityComponent, 'authorizedUsers' | 'dueDateIdPrefix' | 'entities' | 'entityResourceName' | 'hideAddButton' | 'isAutoResolvable' | 'isLoading' | 'newDisabled' | 'onDelete' | 'onDraft' | 'onResolve' | 'onSelect' | 'onSubmit' | 'readonlyTasks' | 'selectedTask' | 'selectedView' | 'tasks' | 'taskType' | 'taskWrapperClass'>;
type DateList = WithBoundArgs<typeof TasksKitListDateComponent, 'authorizedUsers' | 'dueDateIdPrefix' | 'entityResourceName' | 'hideAddButton' | 'isAutoResolvable' | 'isLoading' | 'newDisabled' | 'onDelete' | 'onDraft' | 'onResolve' | 'onSelect' | 'onSubmit' | 'readonlyTasks' | 'selectedTask' | 'selectedView' | 'tasks' | 'taskType' | 'taskWrapperClass'>;
type EntityDateList = WithBoundArgs<typeof TasksKitListDateEntityComponent, 'authorizedUsers' | 'dueDateIdPrefix' | 'entities' | 'entityResourceName' | 'hideAddButton' | 'isAutoResolvable' | 'isLoading' | 'newDisabled' | 'onDelete' | 'onDraft' | 'onResolve' | 'onSelect' | 'onSubmit' | 'readonlyTasks' | 'selectedTask' | 'selectedView' | 'tasks' | 'taskType' | 'taskWrapperClass'>;
export interface TasksKitSignature<T = TTaskListVariation> {
    Args: {
        tasks: UTask[];
        entityResourceName?: IResourceName;
        scrollToDate?: TDate;
        groupingField?: 'dueDate' | 'createdAt';
        entityId?: string | null;
        taskType?: TTaskType;
        isAutoResolvable?: boolean;
        listType?: T;
        isLoading?: boolean;
        disabled?: boolean;
        readonlyTasks?: boolean;
        authorizedUsers?: string[];
        hideAddButton?: boolean;
        filterTypes?: TTaskType[];
        filterOptions?: TTaskType[];
        taskWrapperClass?: string;
        onFilterChange?: (filters: TTaskType[]) => void;
        onDraftTask?: (task: UTask) => void;
        onSaveTask?: (task: UTask) => void;
        onDeleteTask?: (task: UTask) => void;
        onDestroy?: (task: UTask | undefined) => void;
    };
    Blocks: {
        default: [{
                ViewSelect: WithBoundArgs<ComponentLike<TasksKitViewSelectSignature>, 'onViewChange' | 'resolvedCount' | 'selectedView' | 'unresolvedCount' | 'disabled'>;
                SubmitBtn: WithBoundArgs<ComponentLike<TasksKitSubmitBtnSignature>, 'loading' | 'onClick' | 'disabled'>;
                // Pick the right `List` based on the `listType`
                List: T extends 'date' ? DateList : T extends 'date-entity' ? EntityDateList : EntityList;
                Filters?: WithBoundArgs<ComponentLike<TasksKitFiltersSignature>, 'onTaskFilterChange' | 'taskTypes' | 'filterOptions'>;
                initScroll: () => void;
                scrollToTask: () => void;
                selectedTask?: UTask;
                title: string;
                onDraft: (date?: string) => void;
            }];
    };
}
export default class TasksKitComponent extends Component<TasksKitSignature> {
    @service
    intl: IntlService;
    @service
    store: StoreService;
    @service
    bvFlash: BvFlashService;
    @service
    eventStream: EventStreamService;
    @service
    taskService: TaskService;
    @service
    sessionUser: SessionUserService;
    @service
    authorization: AuthorizationService;
    noDateId = this.intl.t('tasksPage.noDueDate');
    dueDateIdPrefix = 'task-';
    sortProperties = [
        'dueDate:asc',
        'createdAt:asc'
    ];
    @tracked
    selectedTask: UTask | undefined;
    @tracked
    selectedView: TSelectedView = 'open';
    @tracked
    taskFilters: string[] = [];
    @sort('filteredTasks', 'sortProperties')
    sortedTasks: UTask[];
    get taskResourceName() {
        return this.args.entityResourceName ? TaskResourceNameForEntityType[this.args.entityResourceName.plural as TaskEntityType] : undefined;
    }
    get canCreateTask() {
        return (this.args.entityResourceName && this.authorization.canStrict('CreateTask', this.args.entityResourceName.plural));
    }
    get filteredTasks() {
        const isArchiveView = this.selectedView === 'resolved';
        const filteredTasks = (this.args.tasks ?? []).filter((t)=>this.filterFn(t) && (isArchiveView ? !!t.resolvedAt : !t.resolvedAt));
        if (this.selectedTask) {
            filteredTasks.push(this.selectedTask);
        }
        return filteredTasks;
    }
    get resolvedTaskCount() {
        return (this.args.tasks ?? []).filter((t)=>this.filterFn(t) && t.resolvedAt).length;
    }
    get unresolvedTaskCount() {
        return (this.args.tasks ?? []).filter((t)=>this.filterFn(t) && !t.resolvedAt).length;
    }
    get indexedEntities() {
        return indexedEntities(this.args.tasks);
    }
    @cached
    get tasksTs(): Record<string, UTask[]> {
        if (this.args.listType === 'entity') {
            return TaskService.indexTasksByEntity(this.sortedTasks);
        }
        return TaskService.indexTasksByDate(this.sortedTasks, this.noDateId, this.groupingField);
    }
    get addTaskIsDisabled() {
        return this.args.tasks.some((t)=>!t?.id) || !this.args.onDraftTask;
    }
    get groupingField() {
        return this.args.groupingField ?? 'createdAt';
    }
    constructor(owner: Owner, args: TasksKitSignature['Args']){
        super(owner, args);
        this.addEventHandlers();
    }
    willDestroy() {
        super.willDestroy();
        this.eventStream.removeHandlers(this);
        this.args.onDestroy?.(this.selectedTask);
    }
    @action
    filterFn(t?: UTask) {
        return (t && (this.taskFilters.length === 0 || (this.taskFilters && this.taskFilters.includes(t.taskType))) && t.id !== this.selectedTask?.id);
    }
    @action
    initScroll() {
        const { scrollToDate } = this.args;
        if (!this.selectedTask) {
            const formattedSelectedDate = scrollToDate ? formatLocalDate(scrollToDate) : undefined;
            if (!formattedSelectedDate || (formattedSelectedDate && !this.tasksTs[formattedSelectedDate]) || (this.args.isAutoResolvable && (this.args.taskType === 'PLANNING_TASK' || this.args.taskType === 'EXECUTION_TASK'))) {
                this.createNewTaskDraft(formattedSelectedDate);
            }
            this.scrollTo(formattedSelectedDate);
        }
    }
    @action
    scrollToTask() {
        if (!this.selectedTask) {
            return;
        }
        if (!this.selectedTask.id && !this.selectedTask[this.groupingField]) {
            this.scrollTo(this.noDateId);
        } else if (this.selectedTask.dueDate) {
            const formattedDueDate = formatLocalDate(this.selectedTask.dueDate);
            this.scrollTo(formattedDueDate);
        }
    }
    @action
    selectTask(task?: UTask, forceDelete = false): void {
        if (this.selectedTask && !task) {
            if (forceDelete && !this.selectedTask.id) {
                this.args.onDeleteTask?.(this.selectedTask);
            } else {
                this.handleSubmit.perform(this.selectedTask);
            }
        } else {
            if (this.selectedTask?.newWithoutComment) {
                this.args.onDeleteTask?.(this.selectedTask);
            }
            this.selectedTask = task;
        }
    }
    @action
    createNewTaskDraft(date?: TDate) {
        if (!this.taskResourceName || !this.args.entityId) {
            return;
        }
        const newTask: UTask = this.store.createRecord(this.taskResourceName.singular, {
            linkedEntityId: this.args.entityId,
            createdAt: moment.utc().toDate(),
            dueDate: date && moment.utc(date, LOCAL_DATE_FORMAT).isValid() ? moment.utc(date, LOCAL_DATE_FORMAT).format(ISO_DATE_FORMAT) : undefined,
            taskType: this.args.taskType,
            resolveIf: 'MANUAL'
        }) as UTask;
        this.selectTask(newTask);
        this.scrollToTask();
        this.args.onDraftTask?.(newTask);
    }
    @action
    filterTasks(taskFilters: TTaskType[]) {
        this.taskFilters = taskFilters;
        this.args.onFilterChange?.(taskFilters);
    }
    handleSubmit = enqueueTask(async (task: UTask)=>{
        try {
            // We delete remove the task if it has been added in the form
            // but not touched at all
            if (task.newWithoutComment) {
                return;
            }
            this.selectedTask = undefined;
            const saved = (await this.taskService.saveTask.perform(task)) as UTask;
            return this.args.onSaveTask?.(saved);
        } catch (error) {
            this._onError(error as string);
        }
    });
    handleResolve = enqueueTask(async (task: UTask)=>{
        try {
            if (task.newWithoutComment) {
                return;
            }
            this.selectedTask = undefined;
            const resolvedTask = (task.resolvedAt ? await this.taskService.resolveTask.perform(task) : await this.taskService.reopenTask.perform(task)) as UTask;
            return this.args.onSaveTask?.(resolvedTask);
        } catch (error) {
            this._onError(error as string);
        }
    });
    handleDelete = enqueueTask(async (task: UTask)=>{
        if (!this.taskResourceName) {
            return;
        }
        try {
            await this.taskService.deleteTask.perform(task);
            this.selectedTask = undefined;
            this.args.onDeleteTask?.(task);
        } catch (error) {
            this._onError(error as string);
        }
    });
    private _onError(error: string): void {
        defaultErrorHandler(error);
        this.bvFlash.error(error);
    }
    private addEventHandlers() {
        if (this.args.entityResourceName) {
            this.eventStream.addHandler(this.args.entityResourceName.plural, TaskEvents.TaskCreated, this.onTaskCreated, this);
            this.eventStream.addHandler(this.args.entityResourceName.plural, TaskEvents.TaskUpdated, this.onTaskUpdated, this);
            this.eventStream.addHandler(this.args.entityResourceName.plural, TaskEvents.TaskDeleted, this.onTaskDeleted, this);
        }
    }
    private async onTaskCreated(payload: {
        [key: string]: any;
    }) {
        await this.triggerOnEvent(payload, async (task: UTask)=>{
            return this.args.onDraftTask?.(task);
        });
    }
    private async onTaskUpdated(payload: {
        [key: string]: any;
    }) {
        await this.triggerOnEvent(payload, async (updatedTask: UTask)=>{
            const task = this.args.tasks.find((t)=>t.id === updatedTask.id);
            if (task) {
                task.comment = updatedTask.comment;
                task.dueDate = updatedTask.dueDate;
                task.resolvedAt = updatedTask.resolvedAt;
            }
        });
    }
    private async onTaskDeleted(payload: {
        [key: string]: any;
    }) {
        await this.triggerOnEvent(payload, (task: UTask)=>{
            return this.args.onDeleteTask?.(task);
        });
    }
    private async triggerOnEvent(payload: {
        [key: string]: any;
    }, action: (task: UTask) => void) {
        const task = normalize('task', payload) as UTask;
        const { createdBy, taskType, linkedEntityId } = task;
        if (createdBy && createdBy.id !== this.sessionUser.userId && this.args.taskType === taskType && linkedEntityId === this.args.entityId) {
            action(task);
        }
    }
    private scrollTo(id?: string) {
        const elementToScrollTo = document.getElementById(`${this.dueDateIdPrefix}${id ?? 'New'}`);
        if (elementToScrollTo) {
            elementToScrollTo.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
            });
        }
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{yield
      (hash
        ViewSelect=(component
          TasksKitViewSelectComponent
          onViewChange=(set this 'selectedView')
          selectedView=this.selectedView
          resolvedCount=this.resolvedTaskCount
          unresolvedCount=this.unresolvedTaskCount
          disabled=@disabled
        )
        SubmitBtn=(component
          TasksKitSubmitBtnComponent
          loading=this.taskService.isSaving
          onClick=(fn this.createNewTaskDraft @scrollToDate)
          disabled=(or @disabled (not this.canCreateTask) this.addTaskIsDisabled)
        )
        List=(if
          (eq @listType 'entity')
          (component
            TasksKitListEntityComponent
            isLoading=@isLoading
            entityResourceName=@entityResourceName
            dueDateIdPrefix=this.dueDateIdPrefix
            tasks=this.tasksTs
            entities=this.indexedEntities
            selectedTask=this.selectedTask
            onSubmit=(perform this.handleSubmit)
            onResolve=(perform this.handleResolve)
            onDelete=(perform this.handleDelete)
            onDraft=this.createNewTaskDraft
            onSelect=this.selectTask
            isAutoResolvable=@isAutoResolvable
            selectedView=this.selectedView
            taskType=@taskType
            disabled=@disabled
            newDisabled=(or @disabled this.addTaskIsDisabled (not this.canCreateTask))
            readonlyTasks=@readonlyTasks
            hideAddButton=@hideAddButton
            authorizedUsers=@authorizedUsers
            taskWrapperClass=@taskWrapperClass
          )
          (if
            (eq @listType 'date-entity')
            (component
              TasksKitListDateEntityComponent
              isLoading=@isLoading
              entityResourceName=@entityResourceName
              dueDateIdPrefix=this.dueDateIdPrefix
              tasks=this.sortedTasks
              entities=this.indexedEntities
              selectedTask=this.selectedTask
              onSubmit=(perform this.handleSubmit)
              onResolve=(perform this.handleResolve)
              onDelete=(perform this.handleDelete)
              onDraft=this.createNewTaskDraft
              onSelect=this.selectTask
              isAutoResolvable=@isAutoResolvable
              selectedView=this.selectedView
              taskType=@taskType
              newDisabled=(or @disabled this.addTaskIsDisabled (not this.canCreateTask))
              readonlyTasks=@readonlyTasks
              hideAddButton=@hideAddButton
              authorizedUsers=@authorizedUsers
              taskWrapperClass=@taskWrapperClass
            )
            (component
              TasksKitListDateComponent
              isLoading=@isLoading
              entityResourceName=@entityResourceName
              dueDateIdPrefix=this.dueDateIdPrefix
              tasks=this.tasksTs
              selectedTask=this.selectedTask
              onSubmit=(perform this.handleSubmit)
              onResolve=(perform this.handleResolve)
              onDelete=(perform this.handleDelete)
              onDraft=this.createNewTaskDraft
              onSelect=this.selectTask
              isAutoResolvable=@isAutoResolvable
              selectedView=this.selectedView
              taskType=@taskType
              disabled=@disabled
              newDisabled=(or @disabled this.addTaskIsDisabled (not this.canCreateTask))
              readonlyTasks=@readonlyTasks
              hideAddButton=@hideAddButton
              authorizedUsers=@authorizedUsers
              taskWrapperClass=@taskWrapperClass
            )
          )
        )
        Filters=(if
          @filterOptions
          (component
            TasksKitFiltersComponent
            onTaskFilterChange=this.filterTasks
            taskTypes=@filterTypes
            filterOptions=@filterOptions
          )
        )
        initScroll=this.initScroll
        scrollToTask=(fn this.scrollToTask this.tasksTs)
        selectedTask=this.selectedTask
        title=(t 'tasksPage.title')
        onDraft=this.createNewTaskDraft
      )
    }}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        TasksKit: typeof TasksKitComponent;
        'tasks-kit': typeof TasksKitComponent;
    }
}
